import {Button} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React, {useEffect, useState} from 'react';

import {Section} from 'constants/NavConstants';
import {FileViewQuery, FileViewQueryVariables} from 'files/workspace/__generated/FileViewQuery';
import {FILE_VIEW_QUERY} from 'files/workspace/FileView';
import {
  QueryResultType,
  SkippableBenchlingQueryResult,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import navigateToURL from 'lib/navigateToURL';
import {expandSection} from 'nav/sidepanel/SidepanelActions';
import notifications from 'notifications';
import ScratchFilePreviewModal from 'pipelines/files/ScratchFilePreviewModal';
import {ScratchFileContent} from 'pipelines/files/ScratchFileTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import type {BasicEvent} from 'util/Types';

type FileNode = ExtractByTypename<FileViewQuery['file'], 'FileNode'>;
type FileQueryResult = SkippableBenchlingQueryResult<FileViewQuery, FileViewQueryVariables>;

function getFileNode(result: FileQueryResult): FileNode | null {
  if (result.type !== QueryResultType.SUCCESS) {
    return null;
  }
  const file = result.data.file;
  if (isInaccessible(file)) {
    return null;
  }
  return file;
}

function hasFailedToLoad(result: FileQueryResult): boolean {
  return (
    result.type === QueryResultType.ERROR ||
    (result.type === QueryResultType.SUCCESS && isInaccessible(result.data.file))
  );
}

const LOAD_FAILED_MESSAGE = 'Could not load this file.';

interface FilePreview {
  scratchFile: ScratchFileContent;
  isOutdated: boolean;
}

function getFilePreview(file: FileNode, fileVersionId: string | undefined): FilePreview {
  const givenVersion = file.versions.find((candidate) => candidate.id === fileVersionId);
  const version = givenVersion ?? file.versions.find((candidate) => candidate.id === file.currentVersionId);
  return {
    scratchFile: version?.scratchFile ?? file.scratchFile,
    isOutdated: givenVersion != null && givenVersion.id !== file.currentVersionId,
  };
}

interface FileInputChipProps {
  fileId: string;
  fileVersionId?: string;
}

export default function FileInputChip({fileId, fileVersionId}: FileInputChipProps): JSX.Element {
  const [opened, {open, close}] = useDisclosure(false);
  const [shouldFetch, setShouldFetch] = useState(false);

  const result = useSkippableBenchlingQuery<FileViewQuery, FileViewQueryVariables>(FILE_VIEW_QUERY, {
    variables: {id: fileId},
    skip: !shouldFetch,
  });
  const file = getFileNode(result);
  const preview = file && getFilePreview(file, fileVersionId);
  const failed = hasFailedToLoad(result);

  useEffect(() => {
    if (opened && failed) {
      notifications.error(LOAD_FAILED_MESSAGE);
      close();
    }
  }, [opened, failed, close]);

  const handleChipClick = (event: BasicEvent): void => {
    event.stopPropagation();
    if (failed) {
      notifications.error(LOAD_FAILED_MESSAGE);
      return;
    }
    setShouldFetch(true);
    open();
  };

  return (
    <>
      <BenchChip id={fileId} onClick={handleChipClick} size={ChipSize.SMALL} />
      <span
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <ScratchFilePreviewModal
          extraActions={
            file != null && (
              <Button
                onClick={() => {
                  close();
                  expandSection(Section.PROJECTS);
                  navigateToURL(file.url);
                }}
                variant="secondary"
                {...getTestProps({component: 'FileInputChip', element: 'open-file'})}
              >
                {preview?.isOutdated ? 'Open latest version' : 'Open file'}
              </Button>
            )
          }
          onClose={close}
          opened={opened}
          scratchFile={preview?.scratchFile ?? null}
        />
      </span>
    </>
  );
}
